import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import PageTitle from "../../components/PageTitle";
import LoadingEllipsis from "../../components/LoadingEllipsis";
import Ghost from "../../components/Ghost";
import BackButton from "../../components/BackButton";
import { User } from "../../types/User";
import { Campaign } from "../../types/Campaigns";
import { Experience } from "../../types/Experience";
import useUserService from "../../services/UserService";
import UserMembershipCard from "../../components/User/UserMembershipCard";
import UserDetailForm from "../../components/User/UserDetailForm";
import GiftedSubscriptionPanel from "../../components/User/GiftedSubscriptionPanel";
import useAnalyticsService from "../../services/AnalyticsService";
import { useUrlDateRange } from "../../hooks/useUrlDateRange";
import DateRangeToolbar from "../../components/DateRangeToolbar";
import {
  AnalyticsRankingMetric,
  DailyLiveStreamStats,
  StreamedMinutesOverTimeResult,
  StreamsOverTimeResult,
  ViewsOverTimeResult,
} from "../../types/Analytics";
import { ymd } from "../../utilities/DateUtilities";
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { PIE_COLORS, PIE_COLOURS_REVERSED } from "../../utilities/GraphUtilities";
import NegativeButton from "../../components/NegativeButton";
import PositiveButton from "../../components/PositiveButton";

export default function AdminUserDetailPage() {
  const { userId } = useParams();
  const {
    getUser,
    getCampaignPreviewsEnabledForUser,
    enableCampaignPreviewsForUser,
    disableCampaignPreviewsForUser,
    getPreviewCampaignsForUser,
  } = useUserService();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [initiated, setInitiated] = useState(false);
  const { startDate, endDate, setRange } = useUrlDateRange(7);
  const [user, setUser] = useState<User | null>(null);
  const {getDailyLiveStreamForUserStats} = useAnalyticsService();
  const [dailyGameStats, setDailyGameStats] = useState<DailyLiveStreamStats | null>(null);
  const [campaignPreviewsEnabled, setCampaignPreviewsEnabled] = useState<boolean | null>(null);
  const [previewCampaigns, setPreviewCampaigns] = useState<Campaign[] | null>(null);

  useEffect(() => {
    if (!initiated) {
      setIsLoading(true);
      setInitiated(true);
    }

    getUser(userId as string)
      .then((user) => {
        setUser(user);
      })
      .catch((error) => {
        console.error("Error fetching user:", error);
      });

    getCampaignPreviewsEnabledForUser(userId as string)
      .then((enabled) => {
        setCampaignPreviewsEnabled(enabled);
      })
      .catch((error) => {
        console.error(
          "Error fetching campaign previews enabled for user:",
          error
        );
      });

    getPreviewCampaignsForUser(userId as string)
      .then((campaigns) => {
        setPreviewCampaigns(campaigns);
      })
      .catch((error) => {
        console.error(
          "Error fetching the campaigns a user can preview:",
          error
        );
        setPreviewCampaigns([]);
      });
  }, [initiated]);

  useEffect(() => {
    if (!startDate || !endDate) return;

    // Get the data.
    getData();
  }, [startDate, endDate]);

  useEffect(() => {
    if (user) {
      setIsLoading(false);
    }
  }, [user]);

  const handleUserSaved = (user: User) => {};

  const getData = async () => {
    if (isLoadingAnalytics) {
      return;
    }

    setIsLoadingAnalytics(true);

    await Promise.all([getLiveAppSummaryData()]);

    setIsLoadingAnalytics(false);
  };

  const getDateList = (): Date[] => {
    const dateList: Date[] = [];
    const currentDate = new Date(startDate!);
    while (currentDate <= endDate!) {
      dateList.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dateList;
  };

  const disableCampaignPreviews = async () => {
    try {
      setCampaignPreviewsEnabled(null);
      await disableCampaignPreviewsForUser(userId as string);
      setCampaignPreviewsEnabled(false);
    } catch (error) {
      console.error(
        "Error disabling campaign previews for user:",
        error
      );
    }
  };

  const enableCampaignPreviews = async () => {
    try {
      setCampaignPreviewsEnabled(null);
      await enableCampaignPreviewsForUser(userId as string);
      setCampaignPreviewsEnabled(true);
    } catch (error) {
      console.error(
        "Error enabling campaign previews for user:",
        error
      );
    }
  };

  const getLiveAppSummaryData = async () => {
    if (!startDate || !endDate) {
      return;
    }

    setIsLoadingAnalytics(true);

    getDailyLiveStreamForUserStats(userId as string, startDate, endDate)
      .then((stats) => {
        setDailyGameStats(stats);
        setIsLoadingAnalytics(false);
      });
  };

  return (
    <>
      <section className="flex flex-col gap-2 divide-stone-900">
        <div className="flex flex-row">
          <BackButton />
          <PageTitle className="text-4xl">
            {user ? user.email : `Loading`} {!user && <LoadingEllipsis />}
          </PageTitle>
        </div>
        <div className="grid grid-cols-1 2xl:grid-cols-3 gap-4">
          <div className="flex flex-col gap-2 col-span-2">
            <h2 className="text-2xl">Details</h2>
            {user ? (
              <UserDetailForm user={user} onSaved={handleUserSaved} />
            ) : (
              <Ghost additionalClasses="h-24 col-span-2 rounded" />
            )}
          </div>
          <div className="flex flex-col gap-2">
            <h2 className="text-2xl">Gifted subscription</h2>
            {user ? (
              <GiftedSubscriptionPanel user={user} />
            ) : (
              <Ghost additionalClasses="h-24 rounded" />
            )}
            <h2 className="text-2xl">All Campaign Previews</h2>
            {campaignPreviewsEnabled !== null ? (
              <div className="flex flex-col gap-2">
                <p>{campaignPreviewsEnabled ? "Enabled - user can see every unapproved campaign in the live app" : "Disabled - user can only see approved campaigns, plus any campaign they have been added to as a test user"}</p>
                {campaignPreviewsEnabled ? (<NegativeButton onClick={disableCampaignPreviews}>Disable</NegativeButton>) : (<PositiveButton onClick={enableCampaignPreviews}>Enable</PositiveButton>)}
              </div>
            ) : (
              <Ghost additionalClasses="h-24 rounded" />
            )}
            <h2 className="text-2xl">Test User For</h2>
            {previewCampaigns !== null ? (
              previewCampaigns.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {previewCampaigns.map((campaign) => (
                    <div
                      key={campaign.id}
                      className="card p-4 flex flex-row justify-between items-center gap-2"
                    >
                      <Link
                        className="grow underline"
                        to={`/admin/campaigns/${campaign.id}`}
                      >
                        {campaign.name}
                      </Link>
                      <p className="text-sm text-white/70">
                        {campaign.isApproved ? "Approved" : "Awaiting approval"}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p>
                  This user hasn't been added as a test user on any campaigns.
                  Test users are managed from the campaign itself.
                </p>
              )
            ) : (
              <Ghost additionalClasses="h-24 rounded" />
            )}
            <h2 className="text-2xl">Roles</h2>
            {user ? (
              user.roles.map((role) => (
                <div
                  key={role}
                  className="card p-4 flex flex-row justify-between items-center gap-2"
                >
                  <div className="flex flex-col gap-2 grow">
                    <p>{role}</p>
                  </div>
                </div>
              ))
            ) : (
              <Ghost additionalClasses="h-24 rounded" />
            )}
            <h2 className="text-2xl">Memberships</h2>
            {user ? (
              user.memberships?.map((membership, idx) => (
                <UserMembershipCard
                  userEmail={user.email}
                  userId={user.id}
                  key={idx}
                  membership={membership}
                />
              ))
            ) : (
              <Ghost additionalClasses="h-24 rounded" />
            )}
            {user && user.memberships?.length === 0 && (
              <p>This user has no memberships</p>
            )}
          </div>
        </div>
      </section>
      <hr className="my-4 border-cyan-300/25" />
      <section className="mt-4">
        <h2 className="text-2xl">Stats</h2>
        {startDate && endDate && (
          <DateRangeToolbar
            startDate={startDate}
            endDate={endDate}
            onChange={setRange}
          />
        )}
        <div className="grid grid-cols-1 xl:grid-cols-2">
          <div className="w-full">
            <h3 className="text-lg">Total Streams</h3>
            <div className="w-full h-64 md:h-80 min-w-0">
              {isLoadingAnalytics && <Ghost additionalClasses="w-full h-full" />}
              {!isLoadingAnalytics && dailyGameStats && (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dailyGameStats.dailyStreams}>
                    <Bar
                      name="Total streams"
                      dataKey="streamCount"
                      fill="#60a5fa"
                    />
                    <Legend />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: "#fff" }}
                      axisLine={{ stroke: "#fff" }}
                      tickLine={{ stroke: "#fff" }}
                    />
                    <YAxis
                      tick={{ fill: "#fff" }}
                      axisLine={{ stroke: "#fff" }}
                      tickLine={{ stroke: "#fff" }}
                    />
                    <Tooltip
                      labelStyle={{ color: "#000" }} // date/header
                      itemStyle={{ color: "#000" }} // series/value rows
                      contentStyle={{
                        backgroundColor: "#fff",
                        borderColor: "#e5e7eb",
                      }} // optional
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
          <div className="w-full">
            <h3 className="text-lg">Duration</h3>
            <div className="w-full h-64 md:h-80 min-w-0">
              {isLoadingAnalytics && <Ghost additionalClasses="w-full h-full" />}
              {!isLoadingAnalytics && dailyGameStats && dailyGameStats.dailyStreams && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={dailyGameStats.dailyStreams}
                    margin={{ top: 28, right: 16, bottom: 0, left: 0 }}
                  >
                    <Line
                      type="monotone"
                      dataKey="totalStreamMinutes"
                      name="Minutes streamed"
                      stroke="#60a5fa" // blue-400
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="totalWatchMinutes"
                      name="Minutes viewed"
                      stroke="#f59e0b" // yellow-500
                      strokeWidth={2}
                      dot={false}
                    />
                    <Legend />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: "#fff" }}
                      axisLine={{ stroke: "#fff" }}
                      tickLine={{ stroke: "#fff" }}
                    />
                    <YAxis
                      tick={{ fill: "#fff" }}
                      axisLine={{ stroke: "#fff" }}
                      tickLine={{ stroke: "#fff" }}
                    />
                    <Tooltip
                      labelStyle={{ color: "#000" }}
                      itemStyle={{ color: "#000" }}
                      contentStyle={{
                        backgroundColor: "#fff",
                        borderColor: "#e5e7eb",
                      }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="w-full">
            <h3 className="text-lg">Game Streams</h3>
            <div className="w-full h-64 md:h-80 min-w-0">
              {isLoadingAnalytics && <Ghost additionalClasses="w-full h-full" />}
              {!isLoadingAnalytics && dailyGameStats && dailyGameStats.sumTitleStreams && (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dailyGameStats.sumTitleStreams}>
                    <Bar
                      name="Total streams"
                      dataKey="streamCount"
                      fill={PIE_COLORS[0]}
                    />
                    <Bar
                      type="monotone"
                      dataKey="totalStreamMinutes"
                      name="Minutes streamed"
                      fill={PIE_COLORS[1]} // blue-400
                      strokeWidth={2}
                    />
                    <Bar
                      type="monotone"
                      dataKey="totalWatchMinutes"
                      name="Minutes viewed"
                      fill={PIE_COLORS[2]} // yellow-500
                      strokeWidth={2}
                    />
                    <Legend />
                    <XAxis
                      dataKey="titleName"
                      tick={{ fill: "#fff" }}
                      axisLine={{ stroke: "#fff" }}
                      tickLine={{ stroke: "#fff" }}
                    />
                    <YAxis
                      tick={{ fill: "#fff" }}
                      axisLine={{ stroke: "#fff" }}
                      tickLine={{ stroke: "#fff" }}
                    />
                    <Tooltip
                      labelStyle={{ color: "#000" }} // date/header
                      itemStyle={{ color: "#000" }} // series/value rows
                      contentStyle={{
                        backgroundColor: "#fff",
                        borderColor: "#e5e7eb",
                      }} // optional
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
          <div className="w-full">
            <h3 className="text-lg">Duration</h3>
            <div className="w-full h-64 md:h-80 min-w-0">
              {isLoadingAnalytics && <Ghost additionalClasses="w-full h-full" />}
              {!isLoadingAnalytics && dailyGameStats && dailyGameStats.sumTitleStreams && (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart margin={{ top: 28, right: 16, bottom: 0, left: 0 }}>
                    <Pie
                      data={dailyGameStats.sumTitleStreams}
                      dataKey="totalStreamMinutes"
                      nameKey="titleName"
                      outerRadius="80%"
                      labelLine={false}
                      label={({ name }) => name}
                      stroke="#0f172a"
                      strokeWidth={1}
                    >
                      {dailyGameStats.sumTitleStreams.map((entry: any, i: number) => (
                        <Cell
                          key={entry.gameId ?? i}
                          fill={PIE_COLORS[i % PIE_COLORS.length]}
                        />
                      ))}
                    </Pie>

                    <Legend />
                    <Tooltip
                      labelStyle={{ color: "#000" }}
                      itemStyle={{ color: "#000" }}
                      contentStyle={{
                        backgroundColor: "#fff",
                        borderColor: "#e5e7eb",
                      }}
                      formatter={(value: any, _name: any, { payload }: any) => [
                        value,
                        payload.titleName,
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
